import type { BooleanInput } from '@angular/cdk/coercion';
import { Directive, booleanAttribute, computed, input } from '@angular/core';
import { hlm } from '@spartan-ng/brain/core';
import { type VariantProps, cva } from 'class-variance-authority';
import type { ClassValue } from 'clsx';

export const badgeVariants = cva(
	'inline-flex items-center border rounded-full px-2.5 py-0.5 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
	{
		variants: {
			variant: {
        primary: 'bg-[#BFDBFA] text-[#003875]',
        secondary: 'bg-[#FFFFFF] text-[#0D0E0F]',
        outline: 'text-[#0D0E0F] border-border',
        destructive: 'bg-[#FBC3C6] text-[#78070D]',
        success: 'bg-[#CAE4BF] text-[#164A00]',
        warning: 'bg-[#FFEACA] text-[#7F5517]',
        accent1: 'bg-[#F9D1CE] text-[#000000]',
        accent2: 'bg-[#FFD499] text-[#663B00]',
        info: 'bg-[#EEEEEF] text-[#000000]',
			},
			size: {
				default: 'text-xs',
				lg: 'text-sm',
			},
			static: { true: '', false: '' },
		},
		compoundVariants: [
			{
        variant: 'primary',
        static: false,
        class: 'hover:bg-primary-200/80',
      },
      {
        variant: 'secondary',
        static: false,
        class: 'hover:bg-gray-100',
      },
      {
        variant: 'destructive',
        static: false,
        class: 'hover:bg-error-200/80',
      },
      {
        variant: 'success',
        static: false,
        class: 'hover:bg-success-200/80',
      },
      {
        variant: 'warning',
        static: false,
        class: 'hover:bg-warning-200/80',
      },
      {
        variant: 'accent1',
        static: false,
        class: 'hover:bg-accent1-200/80',
      },
      {
        variant: 'accent2',
        static: false,
        class: 'hover:bg-accent2-200/80',
      },
		],
		defaultVariants: {
			variant: 'primary',
			size: 'default',
			static: false,
		},
	},
);
export type BadgeVariants = VariantProps<typeof badgeVariants>;

@Directive({
	selector: '[hlmBadge]',
	standalone: true,
	host: {
		'[class]': '_computedClass()',
	},
})
export class HlmBadgeDirective {
	protected readonly _computedClass = computed(() =>
		hlm(badgeVariants({ variant: this.variant(), size: this.size(), static: this.static() }), this.userClass()),
	);

	public readonly userClass = input<ClassValue>('', { alias: 'class' });
	public readonly variant = input<BadgeVariants['variant']>('primary');
	public readonly static = input<BadgeVariants['static'], BooleanInput>(false, { transform: booleanAttribute });
	public readonly size = input<BadgeVariants['size']>('default');
}
